import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, Injector, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ApmModule } from '@elastic/apm-rum-angular';
import { appConstants } from '@mcl-app/app/core/constant/constants';
import { ScreenBookingConfirmationService, SelfBookingServiceClientService, bookingFiltersConfig } from '@mcl/booking-feature';
import { DEFAULT_NAVIGATION_CONFIG, InitAppService } from '@mcl/core';
import {
    AppBasicsModuleConfigModel,
    AuthState,
    USER_INITIALIZATION_SERVICE,
    provideAppBasics,
    provideMdsModal,
    provideMfCommonAuth,
} from '@meddev/fe-shared';
import { MfCaptchaStrategyEnum, provideMfCaptcha } from '@meddev/fe-shared/mf-captcha';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsRouterPlugin } from '@ngxs/router-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { Store, provideStore } from '@ngxs/store';
import { BOOKING_CONFIRMATION_SERVICE, BOOKING_FILTER_CONFIG, BOOKING_SERVICE_TOKEN, BookingState, SelfBookingState } from '@shared/booking-feature';
import {
    APP_CONSTANTS,
    APP_ROUTE_CONSTANTS,
    AuthGuardConfig,
    BaseInitAppService,
    GUARD_CONFIG,
    LANGUAGE_SWITCHER_CONFIG,
    LOGIN_LOGO_COMPONENT,
    MENU_ITEMS,
    NAVIGATION_CONFIG,
    SharedAppState,
    appInitFactory,
} from '@shared/core';
import { AUTH_MANAGER_BASE_TOKEN, InsuranceState, MCAAuthState, initAuthManagerFactory } from '@shared/data';
import { EnvironmentHelper } from '@shared/utils';
import { RecaptchaV3Module } from 'ng-recaptcha-2';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { provideNgxStripe } from 'ngx-stripe';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { LogoComponent } from './component/logo/logo.component';
import { mclMenuItems } from './core/constant/menu-items';
import { appRouteConstants } from './core/constant/route-constants';
import { MclUserInitializationService } from './service/user-initialization.service';

EnvironmentHelper.initEnvironment(environment);

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),
        provideNgxStripe(),
        provideAnimations(),
        provideAppInitializer(() => {
            const initService = inject(BaseInitAppService);
            return appInitFactory(initService)();
        }),
        {
            provide: BaseInitAppService,
            useClass: InitAppService,
        },
        importProvidersFrom(ApmModule, RecaptchaV3Module),
        provideMatomo(
            {
                mode: 'deferred',
            },
            withRouter(),
        ),
        provideAppBasics({
            version: environment.VERSION as string,
            production: environment.production,
            releaseStage: environment.RELEASE_STAGE as AppBasicsModuleConfigModel['releaseStage'],
            versionUpdateCheck: false,
            bugSnag: {
                apiKey: environment.BUGSNAG_KEY,
            },
            matomo: {
                siteId: 33,
                dimensions: {
                    version: 1,
                    majorVersion: 2,
                    businessSubjectId: 3,
                },
            },
            apmServiceName: 'mcl-app',
            errorDefinitions: undefined, // TODO AppBasicsModuleConfigModel is required, should be fixed in shared package or defined here
        }),
        provideMfCommonAuth({
            authV1Url: appConstants.environment.api.url + appConstants.httpClient.authBaseV1,
            authV2Url: appConstants.environment.api.url + appConstants.httpClient.authBase,
            authV2TokenPatterns: [/.*/],
            loginRedirectUrl: '/login',
            realmName: 'MCL',
            clientId: 'mcl',
            impersonationTokenUrlParamName: 'impersonation-token',
        }),
        provideMdsModal({
            default: {
                confirmButtonText: $localize`Confirm`,
                cancelButtonText: $localize`Close`,
                denyButtonText: $localize`Cancel`,
            },
            error: {
                title: $localize`Error`,
            },
        }),
        provideMfCaptcha({
            language: 'en',
            v2: environment.reCaptcha?.v2,
            v3: environment.reCaptcha?.v3,
            defaultStrategy: MfCaptchaStrategyEnum.FALLBACK,
        }),
        provideStore([SharedAppState, AuthState, MCAAuthState, InsuranceState, SelfBookingState, BookingState], {
            developmentMode: !environment.production,
        }),
        withNgxsRouterPlugin(),
        withNgxsLoggerPlugin(),
        withNgxsReduxDevtoolsPlugin(),
        withNgxsStoragePlugin({ keys: ['AuthState', 'MCAAuthState', 'MediaState', 'BookingState'] }),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: AUTH_MANAGER_BASE_TOKEN,
            useFactory: initAuthManagerFactory,
            deps: [Injector, Store],
            multi: false,
        },
        {
            provide: USER_INITIALIZATION_SERVICE,
            useClass: MclUserInitializationService,
        },
        {
            provide: GUARD_CONFIG,
            useValue: {
                loginRoute: appRouteConstants.login,
                redirectAfterLoginParameter: appConstants.redirectAfterLoginParameter,
            } as AuthGuardConfig,
        },
        {
            provide: APP_ROUTE_CONSTANTS,
            useValue: appRouteConstants,
        },
        {
            provide: APP_CONSTANTS,
            useValue: appConstants,
        },
        {
            provide: LOGIN_LOGO_COMPONENT,
            useValue: LogoComponent,
        },
        {
            provide: MENU_ITEMS,
            useValue: mclMenuItems,
        },
        {
            provide: BOOKING_SERVICE_TOKEN,
            useClass: SelfBookingServiceClientService,
        },
        {
            provide: BOOKING_FILTER_CONFIG,
            useValue: bookingFiltersConfig,
        },
        {
            provide: BOOKING_CONFIRMATION_SERVICE,
            useClass: ScreenBookingConfirmationService,
        },
        {
            provide: NAVIGATION_CONFIG,
            useExisting: DEFAULT_NAVIGATION_CONFIG,
        },
        {
            provide: 'LOGIN_INFO',
            useFactory: (store: Store) => store.select(AuthState.tokenData$),
            deps: [Store],
        },
        {
            provide: LANGUAGE_SWITCHER_CONFIG,
            useValue: {
                enabled: false,
            },
        },
    ],
};
